/**
 * Quaternius sofa configurator — real CC0 sofa GLBs, several shape declinations.
 *
 * The point: pick a sofa SHAPE (2-seater, 3-seater, 4-seater, L-shape,
 * sectional XL) and the corresponding GLB(s) swap in live. NOT a scene
 * arrangement — this is a product configurator for a single sofa line.
 *
 * Quaternius ships 5 sofa GLBs in the Ultimate House Interior Pack:
 *   - Couch Small  (×2 variants)
 *   - Couch Medium (×2 variants)
 *   - Couch Large  (renamed to lr-sofa.glb by import-quaternius.mjs)
 *   - L Couch      (pre-assembled L-shape sectional)
 *
 * Filenames map to public/modules/quaternius/*.glb. If a file is missing,
 * ModuleLoader falls back to procedural geometry AND bumps
 * window.__NEOVAA_GLTF_FALLBACKS__.
 */

import type { ProductConfig, ModuleDef } from "@/types";

const GLB_BASE = "/modules/quaternius/";

function module(
  id: string,
  label: string,
  glbName: string,
  scale: number,
  width = 1,
  proceduralKind: ModuleDef["proceduralKind"] = "box",
  proceduralColor = "#bdb3a3",
): ModuleDef {
  return {
    id,
    label,
    glb: GLB_BASE + glbName,
    width,
    scale,
    sockets: [],
    proceduralKind,
    proceduralColor,
  };
}

/* ── Filenames (placeholders — will be updated after the Poly Pizza ZIP is
 * extracted; the extractor renames to these slugs). Each file MUST resolve
 * for the GLB pipeline to count under check 6. */
const F_COUCH_SMALL  = "Couch Small.glb";
const F_COUCH_MEDIUM = "Couch Medium.glb";
const F_COUCH_LARGE  = "lr-sofa.glb";     // = Couch Large.glb renamed by import script
const F_L_COUCH      = "L Couch.glb";

export const QUATERNIUS_LIVING_ROOM: ProductConfig = {
  productId: "quaternius-sofa",
  productLabel: "Modular Sofa — Quaternius CC0",
  description:
    "Real 3D sofa configurator from the Quaternius Ultimate House Interior Pack (CC0). Five shape declinations from compact 2-seater to sectional XL — pick a shape to see the model swap live in the viewer.",
  basePrice: 0,
  currency: "USD",
  leadTime: "Tech demo (CC0 assets)",
  formes: [
    {
      id: "compact",
      label: "Compact 2-seater",
      featured: true,
      priceDelta: 0,
      modules: [
        { moduleId: "couch-small", position: { x: 0, y: 0, z: 0 } },
      ],
    },
    {
      id: "medium",
      label: "3-seater",
      featured: true,
      priceDelta: 0,
      modules: [
        { moduleId: "couch-medium", position: { x: 0, y: 0, z: 0 } },
      ],
    },
    {
      id: "large",
      label: "4-seater",
      featured: true,
      priceDelta: 0,
      modules: [
        { moduleId: "couch-large", position: { x: 0, y: 0, z: 0 } },
      ],
    },
    {
      id: "l-shape",
      label: "L-shape sectional",
      featured: true,
      priceDelta: 0,
      modules: [
        { moduleId: "l-couch", position: { x: 0, y: 0, z: 0 } },
      ],
    },
    {
      id: "sectional",
      label: "Sectional XL (L + 3-seater)",
      featured: true,
      priceDelta: 0,
      modules: [
        { moduleId: "l-couch",      position: { x: 0,   y: 0, z: 0 } },
        { moduleId: "couch-medium", position: { x: 2.4, y: 0, z: 0 } },
      ],
    },
  ],
  // The fabric/leg axes are not active in this CC0 demo (Quaternius bakes its
  // own vertex-colour materials) but ProductConfig requires non-empty arrays.
  fabrics: [
    { id: "as-is", label: "Baked vertex colours", category: "mat", color: "#bdb3a3" },
  ],
  legs: [
    { id: "as-is", label: "Original finish", color: "#5b4a3a" },
  ],
  // Quaternius authors at ~2× metres — normalise to realistic sofa heights
  // (~0.85m tall, 2–3m wide) via uniform 0.4 scale.
  modules: [
    module("couch-small",  "Couch Small",  F_COUCH_SMALL,  0.40, 1.7),
    module("couch-medium", "Couch Medium", F_COUCH_MEDIUM, 0.40, 2.4),
    module("couch-large",  "Couch Large",  F_COUCH_LARGE,  0.40, 3.0),
    module("l-couch",      "L Couch",      F_L_COUCH,      0.40, 3.0),
  ],
};
